import { useEffect, memo, useCallback } from 'react'
import { observer } from 'mobx-react'

import { useSaveButton } from 'hooks/useSaveButton'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import {
  CONFIG_DDF,
  CONFIG_SECOND_DEBTOR,
  isFFGConfigWithAutoRedirectOnDacSuccess
} from 'config/utils/config'
import { NavigateFunction } from 'react-router-dom'
import LegalHintTexts from 'texts/common/copy/legal'
import {
  RegisteredEventName,
  broadcastToParent
} from '@finanzcheck/catalyst-pollard'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import { usePayoutAccountValidation } from 'hooks/usePayoutAccountValidation'
import { getButtonSizes } from './getButtonSizes'
import FormNavigation from './FormNavigationTemplate'
import { getAge } from 'utils/date'
import { AgeGroup } from 'constants/components'
import { useAgeGroup } from 'stores/utils/hooks/useAgeGroup'
import { Button } from '@mui/material'
import { Experiments } from 'CustomOptimizelyProvider'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { CONFIG_DDF_RCE_DAC_CHOICE } from 'config/utils/config'
import { isYoungOrMiddleAgeGroup } from 'stores/helpers/AgeGroups'

type Props = {
  onBack?: () => void
  onNext: () => void
  legalContact: string
  navigate: NavigateFunction
}

const FFGFormNavigation = observer(({ onBack, onNext, navigate }: Props) => {
  const {
    rootStore: {
      isInIframe,
      urlParams: { backUrl },
      traversal: {
        data: {
          debtors: {
            primary: {
              personal: { birthday }
            }
          }
        }
      },
      experimentData,
      page: { config }
    }
  } = useRootStore()

  const {
    page: { isFirstDisplayedPage, isLastDisplayedPage, getCurrentIndex },
    navigation: { currentPageInSession },
    traversal: {
      data,
      dacWidgetState,
      conditionBasedDacDescision,
      conditionBasedDacDescisionHasErrorState,
      setconditionBasedDacDescisionHasErrorState
    }
  } = useStores()

  const ageGroup = useAgeGroup()
  const debtorsAge = getAge(birthday)
  const isYoungAgeConditionMet =
    ageGroup === AgeGroup.YOUNG_AGE_GROUP ||
    ageGroup === AgeGroup.TOO_YOUNG_AGE_GROUP

  const isPayoutAccountReady = usePayoutAccountValidation(true)
  const isPageDAC = currentPageInSession === 'dac'

  const { isSaveButtonVisible } = useSaveButton({
    onSuccess: () => {},
    onError: () => {}
  })

  const currentIndex = getCurrentIndex()

  const trackWithOrigin = (event: AmplitudeEvent, payload = {}) => {
    trackInAmplitude(event, { origin: 'taurine', ...payload })
  }

  const isInCat3818Variation = config.name === CONFIG_DDF_RCE_DAC_CHOICE

  const isInCAT4116Variation =
    experimentData[Experiments.CAT_4116_DAC_FC_ALLIGNMENT] ===
      PossibleVariants.VARIATION_B && config.name === CONFIG_DDF

  const onNextWithTracking = useCallback(() => {
    const isInCat3818VariationError =
      isInCat3818Variation &&
      conditionBasedDacDescision === undefined &&
      isLastPage &&
      isYoungOrMiddleAgeGroup(debtorsAge) // we only check the dac decision error for young and middle age groups

    if (isInCat3818VariationError) {
      setconditionBasedDacDescisionHasErrorState(true)
      return
    }

    if (ageGroup === AgeGroup.MIDDLE_AGE_GROUP && isPageDAC) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'skip_dac_button_click_on_dac_page',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
    }
    onNext()
  }, [onNext, data, conditionBasedDacDescision])

  useEffect(() => {
    if (
      isInCat3818Variation &&
      !conditionBasedDacDescisionHasErrorState &&
      typeof conditionBasedDacDescision !== 'undefined' &&
      !conditionBasedDacDescision
    ) {
      onNextWithTracking()
    }
  }, [
    isInCat3818Variation,
    conditionBasedDacDescision,
    conditionBasedDacDescisionHasErrorState,
    onNextWithTracking
  ])

  const onBackHandler = () => {
    const isFinalizingOnBackButton =
      (dacWidgetState !== 'initial' &&
        isLastPage &&
        ageGroup !== AgeGroup.OLD_AGE_GROUP &&
        !isInCAT4116Variation) ||
      (isInCAT4116Variation &&
        ageGroup === AgeGroup.YOUNG_AGE_GROUP &&
        isPageDAC)

    // clean up after CAT-4116 removal
    if (isInCAT4116Variation && isPageDAC) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: `final_on_back_button_click_${ageGroup.toLowerCase()}`,
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
    }
    if (isLastPage) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'back_button_click_on_dac_page',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
    }
    if (isFinalizingOnBackButton) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'final_on_back_button_click',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
      onNext()
      return
    }
    onBack && onBack()
  }

  const isLastPage = isLastDisplayedPage(currentPageInSession)

  useEffect(() => {
    if (
      (isYoungAgeConditionMet ||
        isFFGConfigWithAutoRedirectOnDacSuccess(config.name)) &&
      dacWidgetState === 'success'
    ) {
      trackWithOrigin(AmplitudeEvent.OnDACSuccessAutoRedirect)
      onNextWithTracking()
    }
  }, [dacWidgetState, config.name])

  const isFirstPage = isFirstDisplayedPage(currentPageInSession)

  const backLabel =
    !isInIframe || currentIndex > 0 || backUrl ? 'Zurück' : undefined

  const isBackButtonHidden =
    !backLabel || isFirstPage || (isInCat3818Variation && isLastPage)

  let submitLabel = isLastPage ? 'Angebote ansehen' : 'Weiter'

  const getIsContinueButtonHidden = () => {
    if (isInCat3818Variation && isLastPage && !conditionBasedDacDescision) {
      submitLabel = 'Weiter zu den Angeboten'
      return false
    }

    if (config.name === CONFIG_SECOND_DEBTOR) {
      return false
    }

    if (config.name === CONFIG_DDF) {
      if (
        (isPageDAC && dacWidgetState !== 'success') ||
        (ageGroup !== AgeGroup.OLD_AGE_GROUP && isLastPage && isPageDAC)
      ) {
        return true
      }

      return false
    }

    return (
      isLastPage &&
      isPayoutAccountReady &&
      dacWidgetState !== 'success' &&
      isYoungOrMiddleAgeGroup(debtorsAge)
    )
  }

  const isContinueButtonHidden = getIsContinueButtonHidden()

  const { sizeOfContinueButton, sizeOfBackButton, sizeOfSaveButton } =
    getButtonSizes(
      isBackButtonHidden,
      isSaveButtonVisible,
      isContinueButtonHidden
    )

  const showSkipDacButton =
    ageGroup === AgeGroup.MIDDLE_AGE_GROUP &&
    isLastPage &&
    isPageDAC &&
    !isInCat3818Variation

  const showDacLegalText =
    isLastPage && isPayoutAccountReady && isPageDAC && !isInCat3818Variation

  return (
    <FormNavigation
      onBack={onBackHandler}
      onNext={onNext}
      navigate={navigate}
      legalText={LegalHintTexts.defaultLegalHint}
      isContinueWithoutDacVisible={false}
      isBackButtonHidden={isBackButtonHidden}
      isContinueButtonHidden={isContinueButtonHidden}
      sizeOfContinueButton={showSkipDacButton ? 12 : sizeOfContinueButton}
      sizeOfBackButton={sizeOfBackButton}
      sizeOfSaveButton={showSkipDacButton ? 12 : sizeOfSaveButton}
      submitLabel={submitLabel}
      backLabel={backLabel}
      onNextWithTracking={onNextWithTracking}
      trackWithOrigin={trackWithOrigin}
      continueButtonColor={'primary'}
      continueWithoutDacButtonVariant={'text'}
      skipDacButton={
        showSkipDacButton ? (
          <Button
            size="large"
            type="button"
            id="test-submit"
            name="test-submit"
            onClick={onNextWithTracking}
            color="primary"
            fullWidth
            variant="contained"
          >
            Ich habe keinen Online-Banking-Zugang
          </Button>
        ) : null
      }
      showDacLegalText={showDacLegalText}
      backButtonVariant="text"
      saveButtonVariant={'outlined'}
    />
  )
})

export default memo(FFGFormNavigation)
